import { useCallback, useEffect, useRef, useState } from 'react';

const usePhotoCapture = ({
  onCapture,
  fileNamePrefix = 'photo',
  videoFileNamePrefix = 'video',
  errorMessage = 'Could not access the camera. Try uploading a photo instead.',
  videoErrorMessage = 'Could not access the camera or microphone for video.',
  enableVideo = false,
} = {}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const videoRecorderRef = useRef(null);
  const videoChunksRef = useRef([]);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraFacing, setCameraFacing] = useState('environment');
  const [cameraError, setCameraError] = useState('');
  const [captureMode, setCaptureMode] = useState('photo');
  const [isVideoRecording, setIsVideoRecording] = useState(false);

  const clearCameraError = useCallback(() => {
    setCameraError('');
  }, []);

  const attachStreamToVideo = useCallback((stream) => {
    if (!videoRef.current) {
      return;
    }

    const video = videoRef.current;
    video.muted = true;

    if (!stream) {
      video.srcObject = null;
      video.onloadedmetadata = null;
      return;
    }

    const play = async () => {
      try {
        await video.play?.();
      } catch (error) {
        // The browser can still begin playback through the video's autoplay behavior.
      }
    };
    video.onloadedmetadata = play;
    video.srcObject = stream;
    play();
  }, []);

  const bindVideoElement = useCallback((videoElement) => {
    videoRef.current = videoElement;
    if (videoElement && streamRef.current) {
      attachStreamToVideo(streamRef.current);
    }
  }, [attachStreamToVideo]);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const closeCamera = useCallback(() => {
    if (videoRecorderRef.current && videoRecorderRef.current.state === 'recording') {
      videoRecorderRef.current.stop();
    }
    setIsVideoRecording(false);
    stopStream();
    attachStreamToVideo(null);
    setCameraOpen(false);
  }, [attachStreamToVideo, stopStream]);

  const openCamera = useCallback(
    async (facing = cameraFacing, mode = 'photo') => {
      setCameraError('');
      try {
        stopStream();
        const constraints = {
          video: { facingMode: { ideal: facing } },
          ...(mode === 'video' && enableVideo ? { audio: true } : {}),
        };
        let stream = null;

        try {
          stream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (error) {
          if (mode === 'video' && enableVideo) {
            stream = await navigator.mediaDevices.getUserMedia({
              video: { facingMode: { ideal: facing } },
            });
          } else {
            throw error;
          }
        }

        streamRef.current = stream;
        attachStreamToVideo(stream);
        setCameraOpen(true);
        setCameraFacing(facing);
        setCaptureMode(mode);
      } catch (error) {
        console.error('Error opening camera:', error);
        setCameraError(mode === 'video' ? videoErrorMessage : errorMessage);
      }
    },
    [attachStreamToVideo, cameraFacing, enableVideo, errorMessage, stopStream, videoErrorMessage]
  );

  const handleCameraClick = useCallback(async () => {
    if (cameraOpen && captureMode === 'photo') {
      closeCamera();
      return;
    }
    await openCamera(cameraFacing, 'photo');
  }, [cameraFacing, cameraOpen, captureMode, closeCamera, openCamera]);

  const handleVideoClick = useCallback(async () => {
    if (!enableVideo) {
      return;
    }

    if (cameraOpen && captureMode === 'video') {
      closeCamera();
      return;
    }

    await openCamera(cameraFacing, 'video');
  }, [cameraFacing, cameraOpen, captureMode, closeCamera, enableVideo, openCamera]);

  const handleFlipCamera = useCallback(async () => {
    const nextFacing = cameraFacing === 'environment' ? 'user' : 'environment';
    setCameraFacing(nextFacing);
    if (cameraOpen) {
      await openCamera(nextFacing, captureMode);
    }
  }, [cameraFacing, cameraOpen, captureMode, openCamera]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    if (!video.videoWidth || !video.videoHeight) {
      setCameraError('The camera is still starting. Wait a moment and try again.');
      return;
    }
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) return;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `${fileNamePrefix}-${Date.now()}.png`, { type: 'image/png' });
      onCapture?.(file, { mediaType: 'image' });
      closeCamera();
    }, 'image/png');
  }, [closeCamera, fileNamePrefix, onCapture]);

  const startVideoRecording = useCallback(async () => {
    if (!enableVideo) {
      return;
    }

    if (!streamRef.current || !cameraOpen || captureMode !== 'video') {
      await openCamera(cameraFacing, 'video');
    }

    if (!streamRef.current) {
      return;
    }

    if (typeof MediaRecorder === 'undefined') {
      setCameraError('Video recording is not supported in this browser.');
      return;
    }

    const preferredMimeTypes = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
    ];
    const supportedMimeType = preferredMimeTypes.find((mimeType) => MediaRecorder.isTypeSupported(mimeType));

    try {
      videoChunksRef.current = [];
      videoRecorderRef.current = new MediaRecorder(
        streamRef.current,
        supportedMimeType ? { mimeType: supportedMimeType } : undefined
      );

      videoRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          videoChunksRef.current.push(event.data);
        }
      };

      videoRecorderRef.current.onstop = () => {
        const blob = new Blob(videoChunksRef.current, {
          type: videoRecorderRef.current?.mimeType || 'video/webm',
        });
        const file = new File([blob], `${videoFileNamePrefix}-${Date.now()}.webm`, {
          type: blob.type || 'video/webm',
        });
        onCapture?.(file, { mediaType: 'video' });
        setIsVideoRecording(false);
        closeCamera();
      };

      videoRecorderRef.current.start();
      setIsVideoRecording(true);
    } catch (error) {
      console.error('Error starting video recording:', error);
      setCameraError('Could not start video recording. Please try again.');
    }
  }, [cameraFacing, cameraOpen, captureMode, closeCamera, enableVideo, onCapture, openCamera, videoFileNamePrefix]);

  const stopVideoRecording = useCallback(() => {
    if (
      videoRecorderRef.current &&
      videoRecorderRef.current.state === 'recording'
    ) {
      videoRecorderRef.current.stop();
      setIsVideoRecording(false);
    }
  }, []);

  useEffect(() => {
    if (!cameraOpen || !streamRef.current || !videoRef.current) {
      return undefined;
    }

    attachStreamToVideo(streamRef.current);

    return () => {
      if (videoRef.current) {
        videoRef.current.onloadedmetadata = null;
      }
    };
  }, [attachStreamToVideo, cameraOpen]);

  useEffect(() => () => {
    if (videoRecorderRef.current && videoRecorderRef.current.state === 'recording') {
      videoRecorderRef.current.stop();
    }
    stopStream();
    attachStreamToVideo(null);
  }, [attachStreamToVideo, stopStream]);

  return {
    cameraOpen,
    cameraFacing,
    cameraError,
    clearCameraError,
    captureMode,
    isVideoRecording,
    videoRef: bindVideoElement,
    canvasRef,
    openCamera,
    closeCamera,
    handleCameraClick,
    handleVideoClick,
    handleFlipCamera,
    capturePhoto,
    startVideoRecording,
    stopVideoRecording,
  };
};

export default usePhotoCapture;
